export const keyframes = {
  /**
   * Пустая анимация-метка: ничего не двигает, нужна только чтобы браузер прислал
   * `animationstart`, когда подставляет в поле сохранённый пароль. Другого события,
   * по которому автозаполнение можно поймать, у нас нет (см. `PasswordInput`).
   */
  onAutoFillStart: {
    from: {},
    to: {},
  },
  spin: {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  pulse: {
    '50%': {
      opacity: '0.5',
    },
  },
  /**
   * Кольцо, расходящееся от кнопки, которую ждём первой: «Invite» у только что созданной
   * сессии (`freshInviteProps`). Тень, а не scale: кнопка стоит в ряду с соседями, и
   * растущая кнопка толкала бы их.
   */
  attentionRing: {
    '0%': {
      boxShadow: '0 0 0 0 color-mix(in srgb, var(--chakra-colors-purple-solid) 70%, transparent)',
    },
    '70%, 100%': {
      boxShadow: '0 0 0 10px transparent',
    },
  },
  ping: {
    '75%, 100%': {
      transform: 'scale(2)',
      opacity: '0',
    },
  },
  bounce: {
    '0%, 100%': {
      transform: 'translateY(-25%)',
      animationTimingFunction: 'cubic-bezier(0.8,0,1,1)',
    },
    '50%': {
      transform: 'none',
      animationTimingFunction: 'cubic-bezier(0,0,0.2,1)',
    },
  },
  'bg-position': {
    from: {
      backgroundPosition: 'var(--animate-from, 1rem) 0',
    },
    to: {
      backgroundPosition: 'var(--animate-to, 0) 0',
    },
  },
  position: {
    from: {
      insetInlineStart: 'var(--animate-from-x)',
      insetBlockStart: 'var(--animate-from-y)',
    },
    to: {
      insetInlineStart: 'var(--animate-to-x)',
      insetBlockStart: 'var(--animate-to-y)',
    },
  },
  'circular-progress': {
    '0%': {
      strokeDasharray: '1, 400',
      strokeDashoffset: '0',
    },
    '50%': {
      strokeDasharray: '400, 400',
      strokeDashoffset: '-100%',
    },
    '100%': {
      strokeDasharray: '400, 400',
      strokeDashoffset: '-260%',
    },
  },
  'expand-height': {
    from: {
      height: '0',
    },
    to: {
      height: 'var(--height)',
    },
  },
  'collapse-height': {
    from: {
      height: 'var(--height)',
    },
    to: {
      height: '0',
    },
  },
  'expand-width': {
    from: {
      width: '0',
    },
    to: {
      width: 'var(--width)',
    },
  },
  'collapse-width': {
    from: {
      height: 'var(--width)',
    },
    to: {
      height: '0',
    },
  },
  'fade-in': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  /**
   * Появление строки списка: чуть снизу и из прозрачности.
   *
   * 9px, а не 20: сдвиг должен читаться как «строка встала на место», а не как приезд издалека.
   * Вместе с шагом задержки в `stagger-children` это даёт ленте направление сверху вниз - ровно
   * то, как её и читают. Двигаем `translate`, а не `margin`: раскладка при этом не пересчитывается.
   */
  'rise-in': {
    from: {
      opacity: 0,
      translate: '0 9px',
    },
    to: {
      opacity: 1,
      translate: '0 0',
    },
  },
  'fade-out': {
    from: {
      opacity: 1,
    },
    to: {
      opacity: 0,
    },
  },
  'slide-from-left-full': {
    from: {
      translate: '-100% 0',
    },
    to: {
      translate: '0 0',
    },
  },
  'slide-from-right-full': {
    from: {
      translate: '100% 0',
    },
    to: {
      translate: '0 0',
    },
  },
  'slide-from-top-full': {
    from: {
      translate: '0 -100%',
    },
    to: {
      translate: '0 0',
    },
  },
  'slide-from-bottom-full': {
    from: {
      translate: '0 100%',
    },
    to: {
      translate: '0 0',
    },
  },
  'slide-to-left-full': {
    from: {
      translate: '0 0',
    },
    to: {
      translate: '-100% 0',
    },
  },
  'slide-to-right-full': {
    from: {
      translate: '0 0',
    },
    to: {
      translate: '100% 0',
    },
  },
  'slide-to-top-full': {
    from: {
      translate: '0 0',
    },
    to: {
      translate: '0 -100%',
    },
  },
  'slide-to-bottom-full': {
    from: {
      translate: '0 0',
    },
    to: {
      translate: '0 100%',
    },
  },
  'slide-from-top': {
    '0%': {
      translate: '0 -0.5rem',
    },
    to: {
      translate: '0',
    },
  },
  'slide-from-bottom': {
    '0%': {
      translate: '0 0.5rem',
    },
    to: {
      translate: '0',
    },
  },
  'slide-from-left': {
    '0%': {
      translate: '-0.5rem 0',
    },
    to: {
      translate: '0',
    },
  },
  'slide-from-right': {
    '0%': {
      translate: '0.5rem 0',
    },
    to: {
      translate: '0',
    },
  },
  'slide-to-top': {
    '0%': {
      translate: '0',
    },
    to: {
      translate: '0 -0.5rem',
    },
  },
  'slide-to-bottom': {
    '0%': {
      translate: '0',
    },
    to: {
      translate: '0 0.5rem',
    },
  },
  'slide-to-left': {
    '0%': {
      translate: '0',
    },
    to: {
      translate: '-0.5rem 0',
    },
  },
  'slide-to-right': {
    '0%': {
      translate: '0',
    },
    to: {
      translate: '0.5rem 0',
    },
  },
  'scale-in': {
    from: {
      scale: '0.95',
    },
    to: {
      scale: '1',
    },
  },
  'scale-out': {
    from: {
      scale: '1',
    },
    to: {
      scale: '0.95',
    },
  },
  /** Волна голосового индикатора: три полосы «дышат» со сдвигом фазы. */
  voiceWave: {
    '0%, 100%': {
      transform: 'scaleY(.3)',
    },
    '50%': {
      transform: 'scaleY(1)',
    },
  },
  pulseWave: {
    '25%': {
      transform: 'scaleY(.6)',
    },
    '50%': {
      transform: 'scaleY(.4)',
    },
    '100%': {
      transform: 'scaleY(.8)',
    },
  },
};
